import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import CloseButton from '../CloseButton';
import {
  DATE_TEMPLATE,
  toDateInput,
  toUnixMillis,
  maskDate,
  isInvalidDate,
} from './DateRangeFilter.utils';
import { dateRanges } from './DateRangeFilter.config';
import classes from './DateRangeFilter.module.scss';

const CalendarIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      fill="currentColor"
      d="M5 22q-.825 0-1.412-.587A1.93 1.93 0 0 1 3 20V6q0-.825.588-1.412A1.93 1.93 0 0 1 5 4h1V3q0-.425.288-.713A.97.97 0 0 1 7 2q.425 0 .713.287Q8 2.575 8 3v1h8V3q0-.425.288-.713A.97.97 0 0 1 17 2q.425 0 .713.287Q18 2.575 18 3v1h1q.825 0 1.413.588Q21 5.175 21 6v14q0 .825-.587 1.413A1.93 1.93 0 0 1 19 22zm0-2h14V10H5zM5 8h14V6H5z"
    />
  </svg>
);

const DateField = ({ label, value, min, max, onChange }) => {
  const pickerRef = useRef(null);
  const invalid = isInvalidDate(value, min, max);

  const openPicker = () => {
    const picker = pickerRef.current;
    if (!picker) return;
    if (typeof picker.showPicker === 'function') {
      picker.showPicker();
    } else {
      picker.focus();
      picker.click();
    }
  };

  return (
    <div
      className={classes.InputGroup + (invalid ? ' ' + classes.Invalid : '')}
    >
      <span className={classes.InputLabel}>{label}</span>
      <input
        type="text"
        inputMode="numeric"
        placeholder={DATE_TEMPLATE}
        maxLength={10}
        value={value}
        onChange={(e) => onChange(maskDate(e.target.value))}
        className={classes.Input}
      />
      {value && (
        <div className={classes.Ghost} aria-hidden="true">
          <span className={classes.GhostTyped}>{value}</span>
          <span className={classes.GhostRest}>
            {DATE_TEMPLATE.slice(value.length)}
          </span>
        </div>
      )}
      <button
        type="button"
        onClick={openPicker}
        className={classes.CalendarButton}
        aria-label={`Pick ${label} date`}
      >
        <CalendarIcon />
      </button>
      <input
        ref={pickerRef}
        type="date"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value)}
        className={classes.HiddenDate}
        tabIndex={-1}
        aria-hidden="true"
      />
    </div>
  );
};

const DateRangeFilter = ({
  onApply,
  onClear,
  initialFrom = null,
  initialTo = null,
  popupAlign = 'right',
  title = 'Draw Date',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [startDate, setStartDate] = useState(toDateInput(initialFrom));
  const [endDate, setEndDate] = useState(toDateInput(initialTo));
  const [coords, setCoords] = useState(null);
  const buttonRef = useRef(null);
  const popupRef = useRef(null);

  const isFilterActive = Boolean(initialFrom || initialTo);

  useEffect(() => {
    if (!isOpen) return undefined;

    const updatePosition = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      const popupWidth = Math.min(420, window.innerWidth - 32);
      const desired =
        popupAlign === 'left' ? rect.left : rect.right - popupWidth;
      const left = Math.max(
        16,
        Math.min(desired, window.innerWidth - 16 - popupWidth)
      );
      setCoords({ top: rect.bottom + 8, left });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    const handleClickOutside = (event) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, popupAlign]);

  const onApplyClick = () => {
    let resolvedEnd = endDate;
    if (startDate && !endDate) {
      resolvedEnd = toDateInput(Date.now());
      setEndDate(resolvedEnd);
    }
    setIsOpen(false);
    onApply(toUnixMillis(startDate), toUnixMillis(resolvedEnd, true));
  };

  const onClearClick = () => {
    setStartDate('');
    setEndDate('');
    setIsOpen(false);
    onClear();
  };

  const onRangeClick = (getRange) => {
    const { from, to } = getRange();
    setStartDate(toDateInput(from.getTime()));
    setEndDate(toDateInput(to.getTime()));
  };

  return (
    <div className={classes.Wrapper}>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((v) => !v);
        }}
        className={classes.FilterButton + (isFilterActive ? ' ' + classes.Active : '')}
        aria-label="Filter by date"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            fill="currentColor"
            d="M11 20a.967.967 0 0 1-.712-.288A.968.968 0 0 1 10 19v-6L4.2 5.6c-.25-.333-.287-.683-.112-1.05S4.567 4 5 4h14c.433 0 .738.183.913.55.175.367.137.717-.113 1.05L14 13v6c0 .283-.096.52-.287.712A.968.968 0 0 1 13 20h-2Zm1-7.7L16.95 6h-9.9L12 12.3Z"
          />
        </svg>
      </button>

      {isOpen &&
        coords &&
        ReactDOM.createPortal(
          <div
            ref={popupRef}
            className={classes.Popup}
            style={{ top: coords.top, left: coords.left }}
          >
            <div className={classes.Header}>
            <div className={classes.HeaderSpacer} />
            <span className={classes.HeaderTitle}>{title}</span>
            <CloseButton onClick={() => setIsOpen(false)} />
          </div>

          <div className={classes.Inputs}>
            <DateField
              label="From"
              value={startDate}
              max={toDateInput(Date.now())}
              onChange={setStartDate}
            />
            <DateField
              label="To"
              value={endDate}
              min={startDate || undefined}
              max={toDateInput(Date.now())}
              onChange={setEndDate}
            />
          </div>

          <div className={classes.RangeRow}>
            {dateRanges.map((range) => (
              <button
                key={range.label}
                type="button"
                onClick={() => onRangeClick(range.getRange)}
                className={classes.RangeButton}
              >
                {range.label}
              </button>
            ))}
          </div>

          <div className={classes.Actions}>
            <button type="button" onClick={onApplyClick} className={`${classes.PrimaryButton} button-primary`}>
              Apply
            </button>
            <button type="button" onClick={onClearClick} className={classes.SecondaryButton}>
              Clear
            </button>
          </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default DateRangeFilter;
